// Component-layer helpers extracted past the project's "extract on third
// use" threshold (P-007 close precedent for `FF`).
//
// `secondaryText` — the meta-text styling pattern (sm size + text-secondary
// color). Six call sites across five phases:
//   - AuthorByline subtitle (P-008)
//   - DayChairItem event line (P-010)
//   - EventRow second line (P-010)
//   - MemberCard company/location/partner stack (P-011)
//   - SponsorCard tagline (P-011)
//   - (sponsors-needed-cta contact text was a 6th consumer + first
//     HtmlString consumer at P-017; the block was dropped at P-037.18
//     D-118. The HtmlString-vs-string param-type broadening it drove
//     remains; commentary's byline and other future consumers retain
//     the option.)
//
// `paddedRow` / `optionalRow` — wrap an HtmlString in a single-cell
// `<tr><td style="${padStyle}">…</td></tr>` row. `paddedRow` is the
// always-render form; `optionalRow` returns `undefined` when content is
// undefined (which the html kernel interpolates as empty). Eight Component
// call sites for `optionalRow` (P-010 EventCard locationRow + ctaRow;
// P-011 FeatureCard dateRow + locationRow + ctaRow; P-011 SponsorCard
// taglineRow + descriptionRow + websiteRow); seven Block call sites for
// `paddedRow` (P-013 commentary headline-to-body + body-to-byline rows;
// P-014 day-chairs single-column gap rows + entries-row-when-intro;
// P-014 member-shoutout eyebrow row + feature row; P-014
// welcome-new-members single-column gap rows). Centralizes the
// `// prettier-ignore` discipline once.
//
// `gridLayout` — padded N-column grid. Three Block-layer use sites at
// P-015 (day-chairs.two-column + welcome-new-members.multi-column +
// event-card-group.column-grid).
//
// **2-column path:** half-gutter padded `<td width="50%">` cells. Outer
// cells flush to the outer-table edges. Odd-count last row pads with
// empty `<td>` cells (the html kernel renders `${undefined}` as empty
// string per render/html.ts).
//
// **3-column path (P-037.17 D-117):** fixed-pixel rhythm
// `190 / 25 / 190 / 25 / 190` (= 620 px inner content) matching the
// system-wide convention for any 3-col Block layout. The row emits five
// `<td>`s: card / spacer / card / spacer / card. Spacer cells are
// `<td width="25"></td>` empty; card cells are `<td width="190" valign="top">`.
// The outer 30 px L/R padding is the consuming Block's responsibility
// (wrap the gridLayout call in `<td style="padding: 0 30px;">`). Total
// rendered width: 30 + 190 + 25 + 190 + 25 + 190 + 30 = 680 px = `tokens.
// layout.containerWidth`. Odd-count last row pads with empty card cells
// (still `width="190"`) so the spacer alignment doesn't shift.
//
// `pickDefined` — strip `undefined`-valued keys from an object, preserving
// per-key type narrowing under `exactOptionalPropertyTypes` (D-006). Many
// call sites at P-015: commentary.renderBylineRow + day-chairs.entryToProps
// + event-card-group.eventToProps + events-list.eventToProps +
// other-notable-events.itemToProps. Spreads cleanly into a target with
// optional fields without re-introducing `string | undefined`. See D-###.
//
// `stackedList` — single-column stacked list with first item flush and
// subsequent items separated by a `padding-top: ${gap}px` paddedRow,
// wrapped in an outer presentation `<table>`. Three Block-layer use sites
// at P-016 close (day-chairs.renderSingleColumnGrid +
// welcome-new-members.renderSingleColumn + chapter-sponsors.
// renderSingleFeature) — extracted at the third-use threshold. Byte-exact
// equivalent to the prior hand-rolled patterns. NOTE: other-notable-events
// uses the same inner pattern but emits its rows DIRECTLY into the outer
// table (no inner `<table>` wrap), so refactoring it to consume
// `stackedList` would change byte form and break byte-exact tests; kept
// inline. See D-###.
//
// All helpers preserve byte-exact output of the inlined patterns they
// replace.
//
// **P-037.23 D-131 / P-037.29 D-137 / post-Phase-Close §item G —
// fallback PNG constants hoisted here.** EventCard, SponsorCard, and
// Big3EventCard all fall back to `/fallback-image.png` when the
// optional source URL is undefined.
//
// **Inset-shadow approach** (revised at post-Phase-Close §item G): the
// shadow is BAKED INTO the image bytes — staff uploads run through
// the resize pipeline's `bakeInsetShadow` flag (see
// `src/lib/image-dimensions.ts` registry + `src/lib/image-resize.ts`),
// composite PNGs bake via `image-composite.ts`'s `applyInsetShadow`
// call, and the static `/fallback-image.png` was pre-baked once via
// the same algorithm (see `scripts/bake-fallback-shadow.html` for the
// regen procedure). No cell-level CSS `box-shadow: inset` anymore —
// fully redundant + Outlook desktop dropped it silently anyway.
//
// `FALLBACK_IMAGE_RAW_SRC` is the un-baked version used ONLY by the
// composite pipelines — those bake the shadow themselves on the final
// composite PNG, and feeding them the already-baked fallback would
// double-apply.

import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { HtmlString } from '../types'

/** P-037.23 D-131 — chapter fallback PNG served from `public/`.
 *  Rendered (with decorative `alt=""`) when an EventCard's `imageUrl`
 *  or a SponsorCard's `logoUrl` is undefined. **Pre-baked with inset
 *  shadow** at post-Phase-Close §item G; replaced at P-037.30 §item D
 *  with a Banks-supplied file (380 × 200 @2x of the 190 × 100 render
 *  cell — supersedes the corrupted bytes from the earlier
 *  preview_eval hex-roundtrip regen attempt that left the prod file
 *  with a truncated IDAT chunk, causing the fallback to render blank
 *  on EventCard / SponsorCard / Big3EventCard fallback arms). To
 *  regen via the bake script, see `scripts/bake-fallback-shadow.html`
 *  + `src/lib/image-shadow.ts` for the algorithm. The composite
 *  pipelines do NOT use this constant — they use
 *  `FALLBACK_IMAGE_RAW_SRC` so the composite's own shadow bake
 *  doesn't double-apply. */
export const FALLBACK_IMAGE_SRC = '/fallback-image.png'

/** Post-Phase-Close §item G — un-baked fallback PNG. Used ONLY by the
 *  composite pipelines (`compositeBig3Image` + `compositeBigHero`):
 *  those bake the shadow themselves on the final composite PNG via
 *  `applyInsetShadow`, so they need a clean source bitmap. Feeding
 *  them the pre-baked `FALLBACK_IMAGE_SRC` would double-apply the
 *  shadow on the rare composite-fallback case. */
export const FALLBACK_IMAGE_RAW_SRC = '/fallback-image-raw.png'

/** P-037.24 D-132 — chapter Big 3 logo fallback PNG served from
 *  `public/`. Used by the Big 3 Canvas-composite pipeline when the
 *  per-card `big3LogoUrl` is unset (mirrors `FALLBACK_IMAGE_SRC`'s
 *  contract for the event image side). Logos render inside a circular
 *  clip-mask, no shadow design — no shadow bake on this asset. */
export const FALLBACK_LOGO_SRC = '/fallback-logo.png'

export function secondaryText(value: string | HtmlString): HtmlString {
  return text({
    text: value,
    size: tokens.type.size.sm,
    color: tokens.color['text-secondary'],
  })
}

export function paddedRow(content: HtmlString, padStyle: string): HtmlString {
  // prettier-ignore
  return html`<tr><td style="${padStyle}">${content}</td></tr>`
}

export function optionalRow(
  content: HtmlString | undefined,
  padStyle: string,
): HtmlString | undefined {
  return content !== undefined ? paddedRow(content, padStyle) : undefined
}

const ROW_GAP = tokens.space[4]
// P-037.18 D-118: 3-col grids use 20 px inter-row gap (system-wide
// convention). chapter-whatsapp's state grid: gap from last group link
// bottom to the SubSectionHeader divider above the next row's cell.
// event-card-group columns=3 inherits the same gap rhythm. Member-list
// + day-chairs hand-rolled per-row dividers reuse this value as the
// padY above/below the rule. 2-col grids stay at ROW_GAP (16 px) —
// the convention is 3-col-specific.
const THREE_COL_ROW_GAP = 20
const HALF_GUTTER = tokens.space[2]
// P-037.17 D-117: 3-column rhythm constants. Inner content area = 620 px
// (= containerWidth 680 − outer 30 L − outer 30 R).
const THREE_COL_CELL_WIDTH = 190
const THREE_COL_GUTTER = 25

function twoColCellSidePad(colIdx: number, halfGutter: number): string {
  if (colIdx === 0) return `padding-right: ${halfGutter}px;`
  return `padding-left: ${halfGutter}px;`
}

function twoColumnGrid<T>(
  items: ReadonlyArray<T>,
  renderItem: (item: T) => HtmlString,
): HtmlString {
  const columns = 2
  const rowCount = Math.ceil(items.length / columns)
  const rows = Array.from({ length: rowCount }, (_, rowIdx) => {
    const topPad = rowIdx === 0 ? '' : `padding-top: ${ROW_GAP}px; `
    const cells = Array.from({ length: columns }, (_, colIdx) => {
      const cellStyle = `${topPad}${twoColCellSidePad(colIdx, HALF_GUTTER)}`
      const item = items[rowIdx * columns + colIdx]
      const content = item !== undefined ? renderItem(item) : undefined
      // prettier-ignore
      return html`<td valign="top" width="50%" style="${cellStyle}">${content}</td>`
    })
    // prettier-ignore
    return html`<tr>${cells}</tr>`
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>`
}

// P-048.6 §A / D-149 — mobile-only padding-top class. Emitted on every
// card cell in `threeColumnGrid`. Desktop has no rule targeting it
// (row-1 cells stay flush-top per the current design); the
// `@media (max-width: 480px) { .s7gc { padding-top: 20px !important } }`
// rule in `src/render/document-shell.ts` adds the padding only when the
// `td[width]` collapse rule stacks cells vertically. Without this,
// chapter-whatsapp's row-1 cells (Southern 7 + Alabama + Georgia)
// butted up against each other on mobile — Banks's Litmus Android
// Outlook preview surfaced this. Class name kept short (3 chars +
// 'gc') to minimize byte impact across the ~10 cells/grid × 3 consumer
// Blocks emission surface.
const GRID_CARD_CELL_CLASS = 's7gc'

function threeColumnGrid<T>(
  items: ReadonlyArray<T>,
  renderItem: (item: T) => HtmlString,
): HtmlString {
  const columns = 3
  const rowCount = Math.ceil(items.length / columns)
  const rows = Array.from({ length: rowCount }, (_, rowIdx) => {
    const withTopPad = rowIdx > 0
    const cardCell = (colIdx: number): HtmlString => {
      const item = items[rowIdx * columns + colIdx]
      const content = item !== undefined ? renderItem(item) : undefined
      // The html kernel escapes interpolated strings, so the `style="..."`
      // attribute can't be folded into a single template via a string
      // variable — branch the template literal so the attribute is part
      // of the trusted template source (same idiom as the divider primitive
      // colSpan branch).
      // prettier-ignore
      return withTopPad
        ? html`<td valign="top" width="${THREE_COL_CELL_WIDTH}" class="${GRID_CARD_CELL_CLASS}" style="padding-top: ${THREE_COL_ROW_GAP}px;">${content}</td>`
        : html`<td valign="top" width="${THREE_COL_CELL_WIDTH}" class="${GRID_CARD_CELL_CLASS}">${content}</td>`
    }
    // prettier-ignore
    const spacerCell = withTopPad
      ? html`<td width="${THREE_COL_GUTTER}" style="padding-top: ${THREE_COL_ROW_GAP}px;"></td>`
      : html`<td width="${THREE_COL_GUTTER}"></td>`
    // prettier-ignore
    return html`<tr>${cardCell(0)}${spacerCell}${cardCell(1)}${spacerCell}${cardCell(2)}</tr>`
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>`
}

/**
 * P-037.18 D-118 (REVISED P-037.29 post-Phase-Close §item A; REVISED AGAIN
 * P-048.4 §B / D-147) — 3-col grid with per-card top borders. Used by
 * member-list, day-chairs, host-committee, prospect-list.
 *
 * **P-048.4 / D-147 restructure rationale.** The prior implementation
 * (P-037.29 §item A) emitted a SEPARATE divider `<tr>` above each card
 * row with 3 explicit rule segments (one per card column) interleaved
 * with empty spacer cells. On desktop the 3 segments form a visually-
 * continuous-with-gutters line. BUT on mobile, the document-shell `@media
 * (max-width: 480px)` rule (`td[width] { width: 100% !important; display:
 * block !important }`) collapses the divider row's 3 `width="190"` cells
 * to 100%-wide blocks stacked vertically — producing 3 horizontal lines
 * stacked above each card row instead of a single segmented line. Banks's
 * Litmus Android Outlook app preview surfaced this as "a group of lines
 * above" each row.
 *
 * **New design.** Move the rule from a separate `<tr>` onto each card
 * cell via `border-top: 1px solid {color};`. On desktop the 3 cards'
 * top borders sit at the same y-position with 25 px gutters between
 * them → identical visual to the prior segmented-line look. On mobile
 * each stacked card cell carries its own top border → exactly one rule
 * per card, no "group of lines."
 *
 * **Padding-around-the-rule rhythm preserved.** The old divider-row
 * pattern had `padding: 0 0 padY 0;` (flush-top first row) or `padding:
 * padY 0;` (subsequent rows) — i.e., padY above the rule (subsequent)
 * + padY below the rule (always). The new design achieves the same
 * rhythm via:
 *   - `padding-top: padY;` on every card cell → padY gap BELOW the
 *     border (above the card content)
 *   - `padding-bottom: padY;` on every non-last-row card cell → padY
 *     gap ABOVE the NEXT row's border (cell-bottom is adjacent to next
 *     cell-top with cellspacing=0)
 *   - First row gets flush-top automatically — there's no previous row
 *     contributing padding above the border, so the border sits at the
 *     block's top edge
 *   - Last row gets `padding-bottom: 0` so no extra space below the
 *     last card's content (the Block's outer padding handles below-grid
 *     spacing)
 *
 * **Empty-slot rule (preserved from §item A).** Empty card slots in
 * partial last rows render as bare `<td valign="top" width="190"></td>`
 * with no border + no padding — keeps Banks's "Cells without any
 * content should not have a rule" intent. The row's height
 * auto-stretches the empty cells to match the populated cell.
 *
 * `divider.padY` = 20 per the system convention (D-118). `divider.color`
 * = `tokens.color['rule-subtle']` (#e5e5e5) per all current consumers.
 *
 * Banks's verbatim intent (preserved across both revisions):
 *   "the rule should only be above records, not below. Cells without
 *    any content, (like blank cells on a row with only one or two
 *    records) should not have a rule."
 *   "the first 3 records line should be flush to the top of the block,
 *    then some padding to the first line of text."
 *   "they need to maintain their rule (or line) above each. There
 *    should not be a group of lines above." (P-048.4 / D-147 ask)
 */
interface DividerOpts {
  color: string
  padY: number
}

function threeColumnGridWithDivider<T>(
  items: ReadonlyArray<T>,
  renderItem: (item: T) => HtmlString,
  div: DividerOpts,
): HtmlString {
  const columns = 3
  const rowCount = Math.ceil(items.length / columns)
  const rows = Array.from({ length: rowCount }, (_, rowIdx) => {
    const isLastRow = rowIdx === rowCount - 1
    const padBottom = isLastRow ? 0 : div.padY
    const cellStyle = `border-top: 1px solid ${div.color}; padding: ${div.padY}px 0 ${padBottom}px 0;`
    // Card cell — populated slots render the item with border-top +
    // padding rhythm; empty trailing slots (odd-count last row) render
    // a BARE sized `<td>` preserving the 190 px column with no border
    // (Banks: "Cells without any content should not have a rule").
    const cardCell = (colIdx: number): HtmlString => {
      const item = items[rowIdx * columns + colIdx]
      if (item === undefined) {
        // prettier-ignore
        return html`<td valign="top" width="${THREE_COL_CELL_WIDTH}"></td>`
      }
      const content = renderItem(item)
      // prettier-ignore
      return html`<td valign="top" width="${THREE_COL_CELL_WIDTH}" style="${cellStyle}">${content}</td>`
    }
    const spacerCell = html`<td width="${THREE_COL_GUTTER}"></td>`
    // prettier-ignore
    return html`<tr>${cardCell(0)}${spacerCell}${cardCell(1)}${spacerCell}${cardCell(2)}</tr>`
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>`
}

export interface GridLayoutOpts {
  /** When set, emit a per-row divider row above each grid row (including
   *  the first). 3-col only. Used by member-list + day-chairs +
   *  host-committee + prospect-list per the P-037.18 D-118 system
   *  convention (REVISED P-037.29 post-Phase-Close §item A — first-row
   *  divider is flush-top + empty card slots get no rule segment above).
   *  See `threeColumnGridWithDivider` jsdoc for the full intent. */
  rowDivider?: DividerOpts
}

export function gridLayout<T>(
  items: ReadonlyArray<T>,
  columns: 2 | 3,
  renderItem: (item: T) => HtmlString,
  opts: GridLayoutOpts = {},
): HtmlString {
  if (columns === 2) {
    return twoColumnGrid(items, renderItem)
  }
  return opts.rowDivider !== undefined
    ? threeColumnGridWithDivider(items, renderItem, opts.rowDivider)
    : threeColumnGrid(items, renderItem)
}

export function pickDefined<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: Exclude<T[K], undefined> } {
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (value !== undefined) {
      result[key] = value
    }
  }
  return result as { [K in keyof T]?: Exclude<T[K], undefined> }
}

export function stackedList<T>(
  items: ReadonlyArray<T>,
  renderItem: (item: T) => HtmlString,
  gapPx: number,
): HtmlString {
  const rows = items.map((item, i) => {
    const inner = renderItem(item)
    if (i === 0) {
      // prettier-ignore
      return html`<tr><td>${inner}</td></tr>`
    }
    return paddedRow(inner, `padding-top: ${gapPx}px;`)
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>`
}
